// components/LandingOverlay.tsx — the "About <scenario>" modal.
//
// Centred overlay. Themed via the same CSS variables as the rest of the
// login surface — the wrapper around the surface (in Login.tsx) sets
// --accent, --ink, --paper, etc., and this overlay reads them. Switching
// scenarios from inside the overlay (via the embedded pill row) propagates
// through the shared ScenarioContext and the colour swap is visible
// immediately. Closing the overlay does NOT reset selection — the picked
// scenario carries back to the underlying Login screen.
//
// Accessibility:
//   - aria-modal="true", role="dialog", labelled by the title id.
//   - Escape key dismisses.
//   - Backdrop click dismisses.
//   - Focus is trapped inside the dialog while open (Tab cycles through
//     focusable descendants).
//   - Focus is moved to the close button on open and restored to the
//     previously-focused element on close.
//
// Phase B: all user-facing strings (about title/tagline/sections/footer
// CTA) come from `scenarios.json` keyed by the effective scenario id.

import { useEffect, useId, useRef, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { styles } from "../styles";
import { useScenario } from "../scenarios/ScenarioContext";
import ScenarioDropdown from "./ScenarioDropdown";

const STOP: (e: React.MouseEvent) => void = (e) => e.stopPropagation();

const srOnly: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

interface LandingOverlayProps {
  open: boolean;
  onClose: () => void;
  /** Optional explicit id (LandingPane's aria-controls). */
  id?: string;
  /**
   * Optional anchor id (without the leading `#`) inside the overlay's
   * scrollable area to jump to when the dialog opens. Used by the
   * `?policy=1` deep-link from external ad creatives to land directly on
   * the "How Ember Hollow differs" section. Instant scroll, not animated
   * — the overlay is just opening, so there's no UX value in animating.
   */
  initialScrollTo?: string;
}

interface AboutSection {
  label: string;
  title: string;
  body: string;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

export default function LandingOverlay({
  open,
  onClose,
  id,
  initialScrollTo,
}: LandingOverlayProps) {
  const { effective } = useScenario();
  const { t } = useTranslation(["scenarios", "common", "auth"]);

  const cardRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const generatedId = useId();
  const dialogId = id ?? generatedId;
  const titleId = `${dialogId}-title`;

  // Escape to close + Tab focus trap.
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const card = cardRef.current;
      if (!card) return;

      const focusables = card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (active === first || !card.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // On open: capture previous focus, move focus into the dialog, lock scroll.
  // On close: restore focus and scroll.
  useEffect(() => {
    if (!open) return;

    previouslyFocused.current =
      (document.activeElement as HTMLElement | null) ?? null;

    // Defer to next tick so the close button is in the DOM.
    const t = window.setTimeout(() => {
      closeBtnRef.current?.focus();
      // If a deep-link target was passed (e.g. `?policy=1` opens the
      // overlay anchored to #how-ember-hollow-differs), scroll the
      // overlay's scrollable region to that anchor. Instant scroll —
      // the dialog is just appearing, so animating would feel sluggish.
      if (initialScrollTo) {
        const scroll = scrollRef.current;
        const target = scroll?.querySelector<HTMLElement>(
          `#${CSS.escape(initialScrollTo)}`,
        );
        if (target) {
          target.scrollIntoView({ behavior: "auto", block: "start" });
        }
      }
    }, 0);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === "function") {
        try {
          prev.focus();
        } catch {
          /* element gone — ignore */
        }
      }
    };
  }, [open, initialScrollTo]);

  if (!open) return null;

  const aboutTitle = t(`scenarios:${effective.id}.aboutTitle`);
  const aboutTagline = t(`scenarios:${effective.id}.aboutTagline`);
  const aboutFooterCta = t(`scenarios:${effective.id}.aboutFooterCta`);
  const sections = t(`scenarios:${effective.id}.aboutSections`, {
    returnObjects: true,
    defaultValue: [] as AboutSection[],
  }) as AboutSection[];

  return (
    <div
      style={styles.modalBackdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={cardRef}
        id={dialogId}
        style={styles.landingOverlayCard}
        onClick={STOP}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          ref={closeBtnRef}
          type="button"
          aria-label={t("common:buttons.close")}
          style={styles.modalCloseBtn}
          onClick={onClose}
        >
          ×
        </button>

        <div style={overlayHeader}>
          <h2 id={titleId} style={srOnly}>{aboutTitle}</h2>
          <ScenarioDropdown variant="hero" label={`${t("common:labels.about", { defaultValue: "About" })}:`} />
          <p style={overlayHeaderTagline}>{aboutTagline}</p>
        </div>

        <div ref={scrollRef} style={styles.landingOverlayScroll}>
          {sections.map((section, i) => (
            <div key={`${effective.id}-${section.title}`}>
              <section style={styles.landingOverlaySection}>
                <div style={styles.landingOverlaySectionLabel}>
                  {section.label}
                </div>
                <h3 style={styles.landingOverlaySectionTitle}>
                  {section.title}
                </h3>
                <p style={styles.landingOverlaySectionBody}>{section.body}</p>
              </section>
              {i < sections.length - 1 && (
                <div style={styles.landingOverlaySectionRule} aria-hidden />
              )}
            </div>
          ))}

          {/* Platform-level differentiator block — sits beneath the
              scenario-themed about-sections. Anchored with an id so the
              `?policy=1` deep-link from external ad creatives can land
              directly on this block. Copy is cross-scenario (memory,
              pricing, tone) so it reads from auth.json, not scenarios.json. */}
          <div style={styles.landingOverlaySectionRule} aria-hidden />
          <section
            id="how-ember-hollow-differs"
            style={styles.landingOverlaySection}
          >
            <h3 style={styles.landingOverlayDifferHeading}>
              {t("auth:landing.howDifferent.label")}
            </h3>

            <div style={styles.landingOverlaySection}>
              <div style={styles.landingOverlaySectionLabel}>
                {t("auth:landing.howDifferent.memoryLabel")}
              </div>
              <h3 style={styles.landingOverlaySectionTitle}>
                {t("auth:landing.howDifferent.memoryTitle")}
              </h3>
              <p style={styles.landingOverlaySectionBody}>
                {t("auth:landing.howDifferent.memoryBody")}
              </p>
            </div>

            <div style={styles.landingOverlaySectionRule} aria-hidden />

            <div style={styles.landingOverlaySection}>
              <div style={styles.landingOverlaySectionLabel}>
                {t("auth:landing.howDifferent.endingsLabel")}
              </div>
              <h3 style={styles.landingOverlaySectionTitle}>
                {t("auth:landing.howDifferent.endingsTitle")}
              </h3>
              <p style={styles.landingOverlaySectionBody}>
                {t("auth:landing.howDifferent.endingsBody")}
              </p>
            </div>

            <div style={styles.landingOverlaySectionRule} aria-hidden />

            <div style={styles.landingOverlaySection}>
              <div style={styles.landingOverlaySectionLabel}>
                {t("auth:landing.howDifferent.tastefulLabel")}
              </div>
              <h3 style={styles.landingOverlaySectionTitle}>
                {t("auth:landing.howDifferent.tastefulTitle")}
              </h3>
              <p style={styles.landingOverlaySectionBody}>
                {t("auth:landing.howDifferent.tastefulBody")}
              </p>
            </div>
          </section>
        </div>

        <div style={styles.landingOverlayFooter}>
          <button
            type="button"
            style={styles.landingOverlayFooterBtn}
            onClick={onClose}
          >
            {aboutFooterCta}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayHeader: CSSProperties = {
  flexShrink: 0,
  padding: "44px 40px 0",
  textAlign: "center",
  borderBottom: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  paddingBottom: 20,
};

const overlayHeaderTagline: CSSProperties = {
  fontStyle: "italic",
  color: "var(--ink-muted, #8a7355)",
  fontSize: "1.02rem",
  textAlign: "center",
  margin: "12px 0 0",
  lineHeight: 1.5,
};
